import React, { useEffect, useRef, useState } from "react";
import { Check, Copy, MessageCircle, Upload, X } from "lucide-react";
import { adminApi } from "./adminApi";
import { BACKEND_URL } from "../../lib/backendUrl";
import BusinessProfilePanel from "../../components/BusinessProfilePanel";

const overlay = {
  position: "fixed", inset: 0, background: "rgba(15,27,45,0.75)", zIndex: 60,
  display: "flex", alignItems: "stretch", justifyContent: "center", padding: 24,
  overflowY: "auto",
};
const modal = {
  background: "white", width: "100%", maxWidth: 720, borderRadius: 14,
  boxShadow: "0 20px 60px rgba(0,0,0,0.35)", overflow: "hidden",
  alignSelf: "flex-start",
};
const headerStyle = {
  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
  padding: "20px 24px", borderBottom: "1px solid #E5E7EB", background: "#F9FAFB",
};
const sectionStyle = { padding: "16px 24px", borderBottom: "1px solid #F3F4F6" };
const labelStyle = { display: "block", fontSize: 11, color: "#6B7280", fontWeight: 600, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 };
const valueStyle = { fontSize: 13, color: "#0F1B2D", fontWeight: 600 };
const ghostBtn = { background: "white", border: "1px solid #D1D5DB", borderRadius: 8, padding: "6px 10px", fontSize: 12, fontWeight: 600, color: "#1E3A5F", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const primaryBtn = { background: "#1E3A5F", color: "white", border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6 };
const successBtn = { background: "#059669", color: "white", border: "none", borderRadius: 8, padding: "10px 18px", fontSize: 13.5, fontWeight: 700, cursor: "pointer" };

const STEPS = [
  { id: "tally_connected",  key: "tally",    title: "1 · Tally Connection",     desc: "Send the customer their unique code & connect-tally link." },
  { id: "gst_connected",    key: "gst",      title: "2 · GST Connection",       desc: "Save the GSTIN against the customer." },
  { id: "zoho_connected",   key: "zoho",     title: "3 · Zoho CRM",             desc: "Generate an OAuth link and share via WhatsApp." },
  { id: "bank_uploaded",    key: "bank",     title: "4 · Bank Statement",       desc: "Upload bank PDF / CSV / XLSX for the first snapshot." },
  { id: "whatsapp_tested",  key: "whatsapp", title: "5 · WhatsApp Bot",         desc: "Send the welcome message and confirm delivery." },
];

function Dot({ on }) {
  return (
    <span style={{
      display: "inline-block", width: 10, height: 10, borderRadius: "50%",
      background: on ? "#10b981" : "#ef4444", marginRight: 8,
    }} />
  );
}

function Toast({ msg, tone = "ok", onClose }) {
  useEffect(() => {
    if (!msg) return undefined;
    const t = setTimeout(onClose, 2400);
    return () => clearTimeout(t);
  }, [msg, onClose]);
  if (!msg) return null;
  return (
    <div data-testid="admin-toast" style={{
      position: "fixed", bottom: 24, right: 24, zIndex: 80,
      background: tone === "error" ? "#DC2626" : "#059669",
      color: "white", padding: "10px 16px", borderRadius: 8,
      fontSize: 13, fontWeight: 600, boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
    }}>{msg}</div>
  );
}

function ParamField({ testid, label, hint, type = "text", placeholder, value, onChange, min, max, fullWidth }) {
  return (
    <div style={{ gridColumn: fullWidth ? "1 / span 2" : "auto" }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>
        {label}
      </label>
      <input
        data-testid={testid}
        type={type}
        placeholder={placeholder}
        value={value ?? ""}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%", height: 36, border: "1px solid #D1D5DB",
          borderRadius: 8, padding: "0 10px", fontSize: 13, color: "#0F1B2D",
        }}
      />
      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>{hint}</div>
    </div>
  );
}

export default function SetupChecklistModal({ customer, onClose, onAfter }) {
  const [checklist, setChecklist] = useState(null);
  const [indicators, setIndicators] = useState(null);
  const [gstin, setGstin] = useState(customer?.gstin || "");
  const [toast, setToast] = useState({ msg: "", tone: "ok" });
  const [completing, setCompleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!customer?.id) return;
    setGstin(customer?.gstin || "");
    (async () => {
      const [cl, ind] = await Promise.all([adminApi.getChecklist(customer.id), adminApi.customerIndicators(customer.id)]);
      const cl_data = cl.ok ? (cl.data?.checklist || {}) : {};
      const ind_data = ind.ok ? (ind.data || {}) : {};
      // Auto-tick checkboxes when the corresponding indicator is already green
      // so the admin doesn't have to manually re-click after Tally/GST connect.
      const indToField = { tally: "tally_connected", gst: "gst_connected", zoho: "zoho_connected", bank: "bank_uploaded", whatsapp: "whatsapp_tested" };
      const merged = { ...cl_data };
      for (const [iKey, fKey] of Object.entries(indToField)) {
        if (ind_data[iKey] && !merged[fKey]) {
          merged[fKey] = true;
          adminApi.saveChecklist(customer.id, fKey, true).catch(() => {});
        }
      }
      setChecklist(merged);
      setIndicators(ind_data);
    })();
  }, [customer?.id, customer?.gstin]);

  // Tally agent state — kept above the early return so hook order is stable.
  const [tallyGen, setTallyGen] = useState(null);
  const [tallyStatus, setTallyStatus] = useState(null);
  const [tallyBusy, setTallyBusy] = useState(false);

  // Company Parameters state
  const [params, setParams] = useState({
    revenue_target: "", employee_count: "", nps_score: "",
    marketing_spend: "", it_spend_monthly: "", new_customers_this_month: "",
    software_stack: "", security_score: "",
    // Current Revenue fact (rendered in ₹ Cr in the form; persisted as raw
    // rupees). P0 ARCHITECTURE REDESIGN (2026-08): the old fixed
    // revenue_target_1yr/3yr/5yr/10yr fields were removed from this form —
    // the canonical Strategic Target now lives only in `strategic_targets`,
    // edited directly on Company Trajectory.
    revenue_last_fy_actual: "",
  });
  // Business profile (adaptivity engine) — lives inside company_parameters.business_profile
  const [businessProfile, setBusinessProfile] = useState({
    primary_business_model: null,
    additional_models:      [],
    modifier_flags:         { imports: false, exports: false, has_physical_retail: false, b2b: false, b2c: false },
  });
  const [paramsBusy, setParamsBusy] = useState(false);
  const [paramsLoaded, setParamsLoaded] = useState(false);
  const [paramsMigrationNeeded, setParamsMigrationNeeded] = useState(false);

  useEffect(() => {
    if (!customer?.id) return;
    let cancelled = false;
    (async () => {
      const res = await adminApi.getCompanyParameters(customer.id);
      if (cancelled) return;
      if (res.ok) {
        if (res.data?.migration_required) setParamsMigrationNeeded(true);
        const cp = res.data?.company_parameters || {};
        const stack = Array.isArray(cp.software_stack) ? cp.software_stack.join(", ") : (cp.software_stack || "");
        // Targets are stored as raw ₹; the form edits them in ₹ Cr for usability.
        const toCr = (rupees) => (rupees === null || rupees === undefined || rupees === "")
          ? "" : (Number(rupees) / 1_00_00_000);
        setParams({
          revenue_target:              cp.revenue_target ?? "",
          employee_count:              cp.employee_count ?? "",
          nps_score:                   cp.nps_score ?? "",
          marketing_spend:             cp.marketing_spend ?? "",
          it_spend_monthly:            cp.it_spend_monthly ?? "",
          new_customers_this_month:    cp.new_customers_this_month ?? "",
          software_stack:              stack,
          security_score:              cp.security_score ?? "",
          revenue_last_fy_actual:      toCr(cp.revenue_last_fy_actual),
        });
        // Hydrate the business profile too — same JSON column, sub-field.
        const bp = cp.business_profile || {};
        setBusinessProfile({
          primary_business_model: bp.primary_business_model || null,
          additional_models:      Array.isArray(bp.additional_models) ? bp.additional_models : [],
          modifier_flags: {
            imports:             !!bp.modifier_flags?.imports,
            exports:             !!bp.modifier_flags?.exports,
            has_physical_retail: !!bp.modifier_flags?.has_physical_retail,
            b2b:                 !!bp.modifier_flags?.b2b,
            b2c:                 !!bp.modifier_flags?.b2c,
          },
        });
      }
      setParamsLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [customer?.id]);

  useEffect(() => {
    if (!customer?.id) return;
    let cancelled = false;
    const refresh = async () => {
      const res = await adminApi.tallyStatus(customer.id);
      if (!cancelled && res.ok) setTallyStatus(res.data);
    };
    refresh();
    const id = setInterval(refresh, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, [customer?.id]);

  if (!customer) return null;
  const code = (customer.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();

  const showToast = (msg, tone = "ok") => setToast({ msg, tone });

  const toggle = async (field) => {
    const next = !checklist?.[field];
    setChecklist((c) => ({ ...(c || {}), [field]: next }));
    const res = await adminApi.saveChecklist(customer.id, field, next);
    if (!res.ok) {
      setChecklist((c) => ({ ...(c || {}), [field]: !next }));
      showToast(res.error || "Could not save.", "error");
    }
  };

  const copy = async (text, label = "Copied") => {
    try { await navigator.clipboard.writeText(text); showToast(label); } catch (_e) { showToast("Copy failed", "error"); }
  };

  const saveGstin = async () => {
    const g = (gstin || "").trim().toUpperCase();
    if (!g) return showToast("Enter a GSTIN first", "error");
    const res = await adminApi.patchCustomer(customer.id, { gstin: g });
    if (res.ok) {
      showToast("GSTIN saved");
      setIndicators((i) => ({ ...(i || {}), gst: true }));
      // Mirror into the unified integrations table so /integrations + the
      // setup wizard step + the dashboard banner all reflect "GST marked".
      try {
        await fetch(`${BACKEND_URL}/api/integrations/connect`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_id: customer.id,
            provider:    "gst",
            credentials: { gstin: g },
            intent_only: true,
          }),
        });
      } catch (_e) { /* best effort */ }
    }
    else showToast(res.error || "Save failed", "error");
  };

  const generateZoho = () => {
    // OAuth client id is intentionally a placeholder until the user provides one.
    const url = `https://accounts.zoho.in/oauth/v2/auth?response_type=code&client_id=COREAI_PLACEHOLDER&scope=ZohoCRM.modules.ALL&redirect_uri=https%3A%2F%2Fcore-platform-dev.preview.emergentagent.com%2Fcallback%2Fzoho&state=${customer.id}`;
    return url;
  };

  const sendZohoWhatsApp = () => {
    const link = generateZoho();
    const phone = (customer.phone || "").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(`Namaste ${customer.name?.split(" ")[0] || "ji"}, please click to connect Zoho CRM with C-ORE: ${link}`);
    window.open(`https://wa.me/${phone}?text=${msg}`, "_blank", "noopener");
  };

  const markZohoConnected = async () => {
    const res = await adminApi.patchCustomer(customer.id, { zoho_connected: true });
    if (res.ok) {
      showToast("Zoho marked connected");
      setIndicators((i) => ({ ...(i || {}), zoho: true }));
      // Same intent mirror — keeps the integrations table consistent.
      try {
        await fetch(`${BACKEND_URL}/api/integrations/connect`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_id: customer.id,
            provider:    "zoho",
            intent_only: true,
          }),
        });
      } catch (_e) { /* best effort */ }
    }
    else showToast(res.error || "Failed", "error");
  };

  const onUploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    const res = await adminApi.ingestBank(customer.id, f);
    setUploading(false);
    if (res.ok) {
      const s = res.data?.summary || {};
      showToast(`Parsed ${s.rows || 0} rows · revenue ₹${s.revenue || 0}`);
      setIndicators((i) => ({ ...(i || {}), bank: true }));
    } else {
      showToast(res.error || "Upload failed", "error");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const sendWhatsapp = async () => {
    const res = await adminApi.sendWelcome(customer.id);
    if (res.ok) { showToast(`Welcome queued for ${res.data.phone || customer.phone}`); setIndicators((i) => ({ ...(i || {}), whatsapp: true })); }
    else showToast(res.error || "Send failed", "error");
  };

  const onComplete = async () => {
    const checkedCount = STEPS.filter((s) => !!checklist?.[s.id]).length;
    if (checkedCount === 0) { showToast("Please complete at least one step (Tally / GST / Bank / Zoho / WhatsApp).", "error"); return; }
    setCompleting(true);
    const res = await adminApi.setupComplete(customer.id);
    setCompleting(false);
    if (res.ok) {
      showToast("Customer activated · welcome email sent");
      setTimeout(() => { onAfter?.(); onClose?.(); }, 800);
    } else showToast(res.error || "Activation failed", "error");
  };

  const Row = ({ step, children }) => (
    <div style={sectionStyle} data-testid={`step-${step.id}`}>
      <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
        <input data-testid={`check-${step.id}`} type="checkbox"
               checked={!!checklist?.[step.id]} onChange={() => toggle(step.id)}
               style={{ width: 16, height: 16, accentColor: "#059669" }} />
        <span style={{ display: "inline-flex", alignItems: "center" }}>
          <Dot on={!!indicators?.[step.key]} />
          <span style={{ fontSize: 14, fontWeight: 700, color: "#0F1B2D" }}>{step.title}</span>
        </span>
      </label>
      <p style={{ fontSize: 12, color: "#6B7280", marginTop: 6, marginBottom: 12 }}>{step.desc}</p>
      <div>{children}</div>
    </div>
  );

  const tallyLink = `${window.location.origin}/connect/tally?code=${code}`;

  const generateTallyConfig = async () => {
    setTallyBusy(true);
    const res = await adminApi.tallyGenerateConfig(customer.id);
    setTallyBusy(false);
    if (res.ok) {
      setTallyGen(res.data);
      showToast(res.data?.email_sent ? `Download link emailed to ${res.data.customer_email}` : "Token ready (email failed — share link manually)", res.data?.email_sent ? "ok" : "error");
      setIndicators((i) => ({ ...(i || {}), tally: true }));
    } else {
      showToast(res.error || "Could not generate config", "error");
    }
  };

  const resendTallyEmail = async () => {
    setTallyBusy(true);
    const res = await adminApi.tallyResendEmail(customer.id);
    setTallyBusy(false);
    if (res.ok && res.data?.email_sent) showToast(`Email resent to ${res.data.customer_email}`);
    else showToast(res.error || "Resend failed", "error");
  };

  const tallyTimeAgo = (iso) => {
    if (!iso) return "never";
    const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const setParam = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  const saveParameters = async () => {
    setParamsBusy(true);
    const toNumber = (v) => {
      if (v === "" || v === null || v === undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const toRupeesFromCr = (v) => {
      const n = toNumber(v);
      return n === null ? null : Math.round(n * 1_00_00_000);
    };
    const stackArr = (params.software_stack || "")
      .split(",").map((s) => s.trim()).filter(Boolean);
    const payload = {
      revenue_target:              toNumber(params.revenue_target),
      employee_count:              toNumber(params.employee_count),
      nps_score:                   toNumber(params.nps_score),
      marketing_spend:             toNumber(params.marketing_spend),
      it_spend_monthly:            toNumber(params.it_spend_monthly),
      new_customers_this_month:    toNumber(params.new_customers_this_month),
      software_stack:              stackArr.length > 0 ? stackArr : null,
      security_score:              toNumber(params.security_score),
      // Current Revenue fact — Cr-denominated in the form, raw rupees in storage.
      revenue_last_fy_actual:      toRupeesFromCr(params.revenue_last_fy_actual),
      // Business adaptivity profile — same JSON column, separate sub-field.
      business_profile:            businessProfile,
    };
    const res = await adminApi.saveCompanyParameters(customer.id, payload);
    setParamsBusy(false);
    if (res.ok) {
      showToast("Parameters saved · dashboard metrics will update on next refresh");
      setParamsMigrationNeeded(false);
    } else {
      if ((res.error || "").includes("company_parameters_column_missing")) {
        setParamsMigrationNeeded(true);
        showToast("Run migrations/company_parameters.sql in Supabase first", "error");
      } else {
        showToast(res.error || "Save failed", "error");
      }
    }
  };

  return (
    <div data-testid="checklist-modal" style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#0F1B2D" }}>{customer.name} · {customer.company_name || "—"}</div>
            <div style={{ marginTop: 4, fontSize: 12, color: "#6B7280" }}>
              {customer.email} · {customer.phone} · {customer.city || ""}{customer.state ? `, ${customer.state}` : ""}
            </div>
            <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 12 }}>
              <span style={labelStyle}>Industry</span><span style={valueStyle}>{customer.industry || "—"}</span>
              <span style={labelStyle}>Revenue</span><span style={valueStyle}>{customer.revenue_range || "—"}</span>
              <span style={labelStyle}>Employees</span><span style={valueStyle}>{customer.employee_range || "—"}</span>
            </div>
          </div>
          <button data-testid="modal-close" onClick={onClose}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#6B7280", padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        {/* Step 1 — Tally */}
        <Row step={STEPS[0]}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
            <button
              data-testid="tally-generate-config"
              onClick={generateTallyConfig}
              disabled={tallyBusy}
              style={{ ...successBtn, padding: "8px 14px", fontSize: 12.5, opacity: tallyBusy ? 0.7 : 1 }}
            >
              {tallyBusy ? "Working…" : (tallyGen ? "Re-generate & Email Config" : "Generate & Email Config")}
            </button>
            {tallyGen && (
              <button
                data-testid="tally-resend-email"
                onClick={resendTallyEmail}
                disabled={tallyBusy}
                style={{ background: "white", border: "1px solid #1D4ED8", color: "#1D4ED8", borderRadius: 8, padding: "8px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
              >
                Resend Email
              </button>
            )}
          </div>

          {tallyGen && (
            <div data-testid="tally-success-banner" style={{ background: "#DCFCE7", border: "1px solid #86EFAC", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
              <div style={{ color: "#166534", fontSize: 13, fontWeight: 600 }}>
                Download link emailed to {tallyGen.customer_email || "—"}.
              </div>
              <div style={{ color: "#166534", fontSize: 12, marginTop: 4 }}>
                The customer should receive it within 1 minute. Check their spam folder if not received.
              </div>
            </div>
          )}

          {tallyGen && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ color: "#6B7280", fontSize: 12, marginBottom: 4 }}>
                Config sent to: <strong>{tallyGen.customer_email}</strong>
              </div>
              <div style={{ color: "#6B7280", fontSize: 11, marginBottom: 6 }}>
                Backup download link — use if customer reports not receiving email
              </div>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <code
                  data-testid="tally-download-url"
                  style={{ flex: 1, background: "#F3F4F6", padding: "6px 10px", borderRadius: 6, fontFamily: "Menlo,monospace", fontSize: 11, color: "#0F1B2D", overflow: "auto", whiteSpace: "nowrap" }}
                >
                  {tallyGen.download_url}
                </code>
                <button
                  data-testid="copy-tally-download-url"
                  style={ghostBtn}
                  onClick={() => copy(tallyGen.download_url, "URL copied")}
                >
                  <Copy size={12} /> Copy URL
                </button>
              </div>
            </div>
          )}

          <div style={{ marginBottom: 10 }}>
            <div style={{ color: "#6B7280", fontSize: 11, marginBottom: 4 }}>Customer Code (first 8 characters of their ID)</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span style={{ background: "#F3F4F6", padding: "6px 10px", borderRadius: 6, fontFamily: "Menlo,monospace", fontSize: 13, color: "#1E3A5F", fontWeight: 700 }}>{code}</span>
              <button data-testid="copy-tally-code" style={ghostBtn} onClick={() => copy(code, "Code copied")}><Copy size={12} /> Copy</button>
              <button data-testid="copy-tally-link" style={ghostBtn} onClick={() => copy(tallyLink, "Link copied")}><Copy size={12} /> Copy connect link</button>
            </div>
          </div>

          {/* Live sync status */}
          <div data-testid="tally-live-status" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "10px 14px", marginTop: 12 }}>
            {tallyStatus?.last_sync ? (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981" }} />
                  <span style={{ color: "#166534", fontSize: 13, fontWeight: 600 }}>
                    Last synced {tallyTimeAgo(tallyStatus.last_sync)} with {tallyStatus.recent_syncs?.[0]?.records_synced ?? 0} records
                  </span>
                </div>
                <div style={{ color: "#6B7280", fontSize: 12, marginTop: 6 }}>
                  {tallyStatus.sync_count_today || 0} syncs today · agent v{tallyStatus.agent_version || "?"}
                </div>
              </>
            ) : (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#EF4444" }} />
                  <span style={{ color: "#B91C1C", fontSize: 13, fontWeight: 600 }}>
                    Never synced — waiting for first connection
                  </span>
                </div>
                <div style={{ color: "#6B7280", fontSize: 12, marginTop: 6 }}>
                  The agent must be running and Tally must be open for the first sync to appear.
                </div>
              </>
            )}
          </div>
        </Row>

        {/* Step 2 — GST */}
        <Row step={STEPS[1]}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input data-testid="gstin-input" value={gstin || ""} onChange={(e) => setGstin(e.target.value.toUpperCase())}
              placeholder="27AAACX0123L1ZP" maxLength={15}
              style={{ flex: 1, height: 36, border: "1px solid #D1D5DB", borderRadius: 8, padding: "0 10px", fontSize: 13 }} />
            <button data-testid="save-gstin" style={primaryBtn} onClick={saveGstin}>Save GSTIN</button>
          </div>
        </Row>

        {/* Step 3 — Zoho */}
        <Row step={STEPS[2]}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button data-testid="zoho-generate" style={ghostBtn} onClick={() => copy(generateZoho(), "Zoho link copied")}>Generate & Copy link</button>
            <button data-testid="zoho-whatsapp" style={primaryBtn} onClick={sendZohoWhatsApp}><MessageCircle size={12} /> Send via WhatsApp</button>
            <button data-testid="zoho-mark" style={ghostBtn} onClick={markZohoConnected}><Check size={12} /> Mark connected</button>
          </div>
        </Row>

        {/* Step 4 — Bank */}
        <Row step={STEPS[3]}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input ref={fileRef} data-testid="bank-file" type="file" accept=".pdf,.csv,.xlsx,.xls"
                   onChange={onUploadFile} style={{ fontSize: 12 }} />
            {uploading && <span style={{ fontSize: 12, color: "#6B7280" }}>Parsing…</span>}
          </div>
        </Row>

        {/* Step 5 — WhatsApp */}
        <Row step={STEPS[4]}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ background: "#F3F4F6", padding: "6px 10px", borderRadius: 8, fontSize: 13, color: "#0F1B2D" }}>{customer.phone}</span>
            <button data-testid="send-welcome" style={primaryBtn} onClick={sendWhatsapp}><MessageCircle size={12} /> Send Welcome Message</button>
          </div>
        </Row>

        {/* Company Parameters — Unlock more CEO/CMO/CTO metrics */}
        <div data-testid="company-parameters-section" style={sectionStyle}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#0F1B2D" }}>
                Company Parameters — Unlock More Metrics
              </div>
              <div style={{ fontSize: 12, color: "#6B7280", marginTop: 4 }}>
                User-supplied numbers that aren't in Tally. Each field unlocks a derived KPI in the CEO / CMO / CTO modules.
              </div>
            </div>
            <button
              data-testid="save-company-parameters"
              onClick={saveParameters}
              disabled={paramsBusy || !paramsLoaded}
              style={{ ...primaryBtn, opacity: (paramsBusy || !paramsLoaded) ? 0.6 : 1 }}
            >
              {paramsBusy ? "Saving…" : "Save Parameters"}
            </button>
          </div>

          {paramsMigrationNeeded && (
            <div data-testid="params-migration-banner" style={{
              marginTop: 10, background: "#FEF3C7", border: "1px solid #FCD34D",
              borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#92400E",
            }}>
              <strong>One-time setup required:</strong> open Supabase → SQL Editor and paste:
              <pre style={{ marginTop: 6, marginBottom: 0, fontFamily: "Menlo,monospace", fontSize: 11, background: "#FFFBEB", padding: 8, borderRadius: 4, overflow: "auto" }}>
{`ALTER TABLE customers
  ADD COLUMN IF NOT EXISTS company_parameters JSONB DEFAULT NULL;`}
              </pre>
            </div>
          )}

          <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2" style={{
            marginTop: 12, gap: 12, alignItems: "start",
          }}>
            <ParamField
              testid="param-revenue-target" label="Revenue Target (₹)"
              hint="Annual revenue target. Powers Revenue vs Target in CEO module."
              type="number" placeholder="60000000"
              value={params.revenue_target}
              onChange={(v) => setParam("revenue_target", v)}
            />
            <ParamField
              testid="param-employee-count" label="Total Employees"
              hint="Headcount. Powers Revenue per Employee in CEO module."
              type="number" placeholder="84"
              value={params.employee_count}
              onChange={(v) => setParam("employee_count", v)}
            />
            <ParamField
              testid="param-nps" label="NPS Score"
              hint="Net Promoter Score from your last customer survey (-100 to +100)."
              type="number" placeholder="67" min={-100} max={100}
              value={params.nps_score}
              onChange={(v) => setParam("nps_score", v)}
            />
            <ParamField
              testid="param-marketing-spend" label="Marketing Spend (₹/month)"
              hint="Monthly marketing budget. Powers CAC calculation in CMO module."
              type="number" placeholder="50000"
              value={params.marketing_spend}
              onChange={(v) => setParam("marketing_spend", v)}
            />
            <ParamField
              testid="param-it-spend" label="IT Spend (₹/month)"
              hint="Total monthly SaaS + IT spend. Powers IT as % of Revenue in CTO."
              type="number" placeholder="85000"
              value={params.it_spend_monthly}
              onChange={(v) => setParam("it_spend_monthly", v)}
            />
            <ParamField
              testid="param-new-customers" label="New Customers This Month"
              hint="New paying customers acquired this month."
              type="number" placeholder="3"
              value={params.new_customers_this_month}
              onChange={(v) => setParam("new_customers_this_month", v)}
            />
            <ParamField
              testid="param-software-stack" label="Software Stack"
              hint="Comma-separated list of tools your business uses."
              type="text" placeholder="Tally, Microsoft 365, WhatsApp Business"
              value={params.software_stack}
              onChange={(v) => setParam("software_stack", v)}
              fullWidth
            />
            <ParamField
              testid="param-security-score" label="Security Score (0-100)"
              hint="From your last IT security audit (0-100)."
              type="number" placeholder="72" min={0} max={100}
              value={params.security_score}
              onChange={(v) => setParam("security_score", v)}
            />
          </div>

          {/* Business adaptivity profile — primary + additional + flags */}
          <BusinessProfilePanel
            value={businessProfile}
            onChange={setBusinessProfile}
            disabled={paramsBusy || !paramsLoaded}
          />

          {/* Current Revenue fact — powers Company Trajectory's "Last FY
              Actual" chip. P0 ARCHITECTURE REDESIGN (2026-08): the fixed
              1/3/5/10-year target fields that used to live here were
              removed — Company Trajectory now reads the ONE canonical
              Strategic Target (set by the founder directly on the
              Trajectory page's "Edit Target"), never a system-defined
              horizon set from here. */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px dashed #E5E7EB" }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, color: "#0F1B2D" }}>
              Current Revenue — Powers Company Trajectory
            </div>
            <div style={{ fontSize: 11.5, color: "#6B7280", marginTop: 3 }}>
              Last completed FY's actual revenue — the "Today" baseline Company Trajectory paces against. The Strategic Target itself is set by the founder directly on the Trajectory page.
            </div>
            <div className="grid grid-cols-1 tablet:grid-cols-2 desktop:grid-cols-2" style={{
              marginTop: 12, gap: 12, alignItems: "start",
            }}>
              <ParamField
                testid="param-last-fy-actual" label="Last Completed FY — Actual Revenue (₹ Cr)"
                hint="Your 'Today' baseline. Tally rarely has a full prior-year figure live, so set it here."
                type="number" placeholder="18.2"
                value={params.revenue_last_fy_actual}
                onChange={(v) => setParam("revenue_last_fy_actual", v)}
              />
            </div>
          </div>
        </div>


        <div style={{ padding: "16px 24px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#F9FAFB" }}>
          <div style={{ fontSize: 12, color: "#6B7280" }}>
            {STEPS.filter((s) => !!checklist?.[s.id]).length} / 5 complete
          </div>
          <button data-testid="complete-setup" disabled={completing} onClick={onComplete} style={{ ...successBtn, opacity: completing ? 0.7 : 1 }}>
            {completing ? "Activating…" : "Complete Setup → Activate"}
          </button>
        </div>
      </div>
      <Toast msg={toast.msg} tone={toast.tone} onClose={() => setToast({ msg: "", tone: "ok" })} />
    </div>
  );
}
